import React from "react";
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
  IonItem,
  IonIcon,
} from "@ionic/react";
import { mailOutline } from "ionicons/icons";
import { useForm, Controller } from "react-hook-form";
import { useIonRouter } from "@ionic/react";
import {
  useCheckEmail,
  useSendRegistrationCode,
  useSendLoginCode,
} from "@/hooks";
import { getErrorMessage } from "@/api/client";
import { registrationStorage } from "@/services/storage";
import "./Auth.css";
import AppLogo from "@components/AppLogo.tsx";
import { COPYRIGHT_TEXT } from "@/config/config.ts";

interface WelcomeForm {
  email: string;
}

const Welcome: React.FC = () => {
  const router = useIonRouter();
  const checkEmailMutation = useCheckEmail();
  const sendRegistrationCodeMutation = useSendRegistrationCode();
  const sendLoginCodeMutation = useSendLoginCode();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<WelcomeForm>({
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: WelcomeForm) => {
    try {
      // First check if email exists
      const result = await checkEmailMutation.mutateAsync(data.email);

      if (result.exists) {
        // User exists, send login code and navigate to enter code
        await sendLoginCodeMutation.mutateAsync(data.email);
        await registrationStorage.setEmail(data.email);
        router.push("/auth/enter-code", "forward");
      } else {
        // New user, send registration code and navigate to verify email
        await sendRegistrationCodeMutation.mutateAsync(data.email);
        router.push("/auth/verify-email", "forward");
      }
    } catch {
      // Error handled by mutation
    }
  };

  const isPending =
    checkEmailMutation.isPending ||
    sendRegistrationCodeMutation.isPending ||
    sendLoginCodeMutation.isPending;

  const error =
    checkEmailMutation.error ||
    sendRegistrationCodeMutation.error ||
    sendLoginCodeMutation.error;

  return (
    <IonPage>
      <IonContent className="auth-content ion-padding">
        <div className="auth-container">
          <div className="auth-header">
            <AppLogo />
            <IonText color="dark">
              <h1>Welkom</h1>
              <p>Voer je e-mailadres in om door te gaan</p>
            </IonText>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <IonItem className="auth-input" lines="none">
              <IonIcon icon={mailOutline} slot="start" />
              <Controller
                name="email"
                control={control}
                rules={{
                  required: "E-mailadres is verplicht",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Ongeldig e-mailadres",
                  },
                }}
                render={({ field }) => (
                  <IonInput
                    {...field}
                    type="email"
                    placeholder="E-mailadres"
                    inputMode="email"
                    autocomplete="email"
                    onIonInput={(e) => field.onChange(e.detail.value)}
                  />
                )}
              />
            </IonItem>
            {errors.email && (
              <IonText color="danger" className="error-text">
                <p>{errors.email.message}</p>
              </IonText>
            )}

            {error && (
              <IonText color="danger" className="error-text">
                <p>{getErrorMessage(error)}</p>
              </IonText>
            )}

            <IonButton
              expand="block"
              type="submit"
              className="auth-button"
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <IonSpinner name="crescent" />
                  <span style={{ marginLeft: "8px" }}>Laden...</span>
                </>
              ) : (
                "Volgende"
              )}
            </IonButton>
          </form>

          <div className="auth-footer">
            <IonText color="medium">
              <p>{COPYRIGHT_TEXT}</p>
            </IonText>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Welcome;
